import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, SkipForward, Tv, Megaphone, ShieldAlert, Sparkles } from 'lucide-react';

// ─── STAGE 3 · Sign-In Broadcast Engine ───────────────────────────────────
// Permanent 3-page brand-intro loop (Misteck 3.3s → All in One 3.3s → Super App
// 3.4s = 10s) that rotates seamlessly into 15-second sponsor business ad reels,
// then cycles. Used on the sign-in viewport (autoBroadcast). The dashboard keeps
// the manual play/pause/skip widget (autoBroadcast=false).
const BRAND_INTRO = [
  { id: 'brand-misteck',  type: 'brand', title: 'MISTECK',     desc: 'The all-in-one creator studio network', badge: 'MISTECK', color: '#FFD028', ms: 3300 },
  { id: 'brand-allinone', type: 'brand', title: 'ALL IN ONE',  desc: 'One login · every studio, tool & ledger', badge: 'ALL IN ONE', color: '#00E5FF', ms: 3300 },
  { id: 'brand-superapp', type: 'brand', title: 'SUPER APP',   desc: 'Create · earn coins · cash out', badge: 'SUPER APP', color: '#A855F7', ms: 3400 },
];

const SPONSOR_ADS = [
  { id: 'ad-rog',    type: 'advertising', title: 'SPONSOR · ROG ALLY X', desc: 'Unleash absolute mobile gaming apex performance', badge: 'SPONSORED', color: '#FFCC00', ms: 15000 },
  { id: 'ad-milink', type: 'advertising', title: 'SPONSOR · MILINK PAY',  desc: 'Instant coin-to-cash settlement, launching soon', badge: 'SPONSORED', color: '#FFCC00', ms: 15000 },
];

// Broadcast timeline: 10s brand opener → 15s sponsor reels → loop.
const BROADCAST = [...BRAND_INTRO, ...SPONSOR_ADS];

const THEMES = {
  walkthrough: { badge: 'WALKTHROUGH', color: '#00E5FF', bg: 'bg-[#00E5FF]/10', text: 'text-[#00E5FF]' },
  advertising: { badge: 'SPONSORED',   color: '#FFCC00', bg: 'bg-[#FFCC00]/10', text: 'text-[#FFCC00]' },
  platform:    { badge: 'MIST UPDATE', color: '#A855F7', bg: 'bg-[#A855F7]/10', text: 'text-[#A855F7]' },
  brand:       { badge: 'MISTECK',     color: '#FFD028', bg: 'bg-[#FFD028]/10', text: 'text-[#FFD028]' },
};

const MANUAL_PLAYLIST = [
  { id: 'wt-1', type: 'walkthrough', title: 'MISTECK GUIDE', desc: 'Syncing Studio Controls to live workspace feeds' },
  { id: 'ad-1', type: 'advertising', title: 'SPONSOR: ROG ALLY X', desc: 'Unleash absolute mobile gaming apex performance' },
  { id: 'brand-1', type: 'platform', title: 'MIST_COMMAND PRO', desc: 'Phase 32 live tax integration dropping soon' },
];

export default function MisteckSuperAppReel({ isAdmin = false, autoBroadcast = false }) {
  const playlist = autoBroadcast ? BROADCAST : MANUAL_PLAYLIST;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const timerRef = useRef(null);

  const currentItem = playlist[currentIndex];
  const currentTheme = THEMES[currentItem.type] || THEMES.platform;
  const badge = currentItem.badge || currentTheme.badge;
  const accent = currentItem.color || currentTheme.color;
  const isBrand = currentItem.type === 'brand';

  // Auto-broadcast transition engine — advances on each slide's exact duration.
  useEffect(() => {
    if (!autoBroadcast || !isPlaying) return;
    timerRef.current = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % playlist.length);
    }, currentItem.ms || 10000);
    return () => clearTimeout(timerRef.current);
  }, [autoBroadcast, isPlaying, currentIndex, currentItem.ms, playlist.length]);

  return (
    <div className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl overflow-hidden shadow-2xl relative" data-testid="super-app-reel">

      {/* ─── ROLE INTERACTION CHECK (ADMIN CONTEXT) ─── */}
      {isAdmin && (
        <div className="absolute top-2 left-2 z-30 bg-black/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-[#FFCC00]/30 flex items-center gap-2" data-testid="super-app-reel-admin-bar">
          <ShieldAlert className="w-3.5 h-3.5 text-[#FFCC00]" />
          <span className="text-[10px] text-white font-black tracking-widest uppercase">Admin: Slot Config Active</span>
        </div>
      )}

      {/* ─── THE UNIFIED SUPER FRAME BROADCAST WINDOW ─── */}
      <div className="relative aspect-video w-full max-h-[240px] bg-gradient-to-b from-[#111] to-[#050505] flex flex-col justify-between p-4">

        {/* Top Header Row / Active Channel Indicators */}
        <div className="flex justify-between items-center z-10 w-full pt-8 sm:pt-0">
          <span
            className="px-2 py-0.5 rounded text-[10px] font-black tracking-widest transition-colors duration-500"
            style={{ backgroundColor: `${accent}1A`, color: accent }}
            data-testid="super-app-reel-badge"
          >
            {badge}
          </span>
          <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full border border-white/5">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-[10px] text-zinc-400 font-bold tracking-widest">MIST_SUPER_REEL</span>
          </div>
        </div>

        {/* Backdrop Visual Brand Vectors */}
        <div key={currentItem.id} className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-700"
          style={{ opacity: isBrand ? 0.06 : 0.03 }}>
          {currentItem.type === 'walkthrough' && <Tv className="w-32 h-32" style={{ color: accent }} />}
          {currentItem.type === 'advertising' && <Megaphone className="w-32 h-32" style={{ color: accent }} />}
          {(currentItem.type === 'platform' || currentItem.type === 'brand') && <Sparkles className="w-32 h-32" style={{ color: accent }} />}
        </div>

        {/* Brand intro wordmark (broadcast opener only) */}
        {isBrand && (
          <div key={`w-${currentItem.id}`} className="absolute inset-0 flex flex-col items-center justify-center z-[5] px-4 text-center animate-[fadeIn_0.6s_ease-out]" data-testid="super-app-reel-brand">
            <h3 className="font-serif font-black text-white leading-none tracking-tight text-3xl sm:text-4xl" style={{ textShadow: `0 0 24px ${accent}55` }}>
              {currentItem.title}
            </h3>
            <span className="mt-2 inline-block w-10 h-[3px] rounded-full" style={{ backgroundColor: accent }} />
          </div>
        )}

        {/* ─── CONTENT LAYER (title + controls) ─── */}
        <div className="z-10 bg-gradient-to-t from-black via-black/50 to-transparent -mx-4 -mb-4 px-4 pb-4 pt-8">
          <div className="flex justify-between items-end gap-4">
            <div className="flex-1 min-w-0">
              <h4 className="text-white text-xs font-black tracking-widest uppercase truncate mb-0.5" data-testid="super-app-reel-title">
                {currentItem.title}
              </h4>
              <p className="text-zinc-400 text-[11px] font-medium line-clamp-1">
                {currentItem.desc}
              </p>
            </div>

            {/* Media Playback Controls (Motion & Pause Compliant) */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="p-1.5 bg-white text-black hover:bg-[#FFCC00] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#FFCC00]"
                aria-label={isPlaying ? "Pause layout reel rotation" : "Play layout reel rotation"}
                data-testid="super-app-reel-playpause"
              >
                {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => setCurrentIndex((prev) => (prev + 1) % playlist.length)}
                className="p-1.5 bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-500"
                aria-label="Skip to next super app item"
                data-testid="super-app-reel-skip"
              >
                <SkipForward className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* Broadcast progress bar (auto mode only) — re-keys per slide to restart */}
        {autoBroadcast && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/10 z-20" data-testid="super-app-reel-progress">
            <div
              key={currentItem.id}
              className="h-full"
              style={{
                backgroundColor: accent,
                animation: isPlaying ? `mistReelProgress ${currentItem.ms || 10000}ms linear forwards` : 'none',
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes mistReelProgress { from { width: 0%; } to { width: 100%; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
